import React from 'react';
import type { TFunction } from 'i18next';
import { getVehiclePlateNumber } from '@hero/core';
import { cn } from '@hero/web-lib';
import { CheckIcon } from '@hero/web-icons';
import type { DriverStatus, VehicleStatus } from '@hero/schema';

/** Same plate formatting as admin vehicles / orders tables (`letters | numbers | arabic`). */
export function formatPlateForDisplay(plate: string | undefined | null): string {
  if (plate == null || String(plate).trim() === '') return '';
  return getVehiclePlateNumber(String(plate)).displayAll;
}

export type ChipTone = 'success' | 'warning' | 'muted';

const chipToneClass: Record<ChipTone, string> = {
  success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  muted: 'bg-gray-100 text-gray-600 border-gray-200',
};

export function AvailabilityChip({ label, tone }: { label: string; tone: ChipTone }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-medium leading-tight',
        chipToneClass[tone],
      )}>
      {label}
    </span>
  );
}

export function driverStatusChip(
  t: TFunction,
  status: DriverStatus | undefined,
): { label: string; tone: ChipTone } {
  switch (status) {
    case 'busy':
      return { label: t('m-driver:status.busy'), tone: 'warning' };
    case 'active':
      return { label: t('m-driver:status.available'), tone: 'success' };
    case 'inactive':
      return { label: t('m-driver:status.inactive'), tone: 'muted' };
    case 'pending':
      return { label: t('m-driver:status.pending'), tone: 'muted' };
    case 'blocked':
      return { label: t('m-driver:status.blocked'), tone: 'muted' };
    default:
      return { label: t('m-driver:status.available'), tone: 'success' };
  }
}

export function vehicleStatusChip(
  t: TFunction,
  status: VehicleStatus | undefined,
): { label: string; tone: ChipTone } {
  switch (status) {
    case 'busy':
      return { label: t('m-vehicle:status.busy'), tone: 'warning' };
    case 'active':
      return { label: t('m-vehicle:status.active'), tone: 'success' };
    case 'pending':
      return { label: t('m-vehicle:status.pending'), tone: 'muted' };
    case 'maintenance':
      return { label: t('m-vehicle:status.maintenance'), tone: 'muted' };
    case 'inactive':
      return { label: t('m-vehicle:status.inactive'), tone: 'muted' };
    default:
      return { label: t('m-vehicle:status.active'), tone: 'success' };
  }
}

export function SelectRowCheck({ isSelected }: { isSelected: boolean }) {
  return (
    <CheckIcon
      className={cn(
        'size-4 shrink-0 text-primary ms-auto',
        isSelected ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}
